/**
 * Annotations API Service
 *
 * API client functions for transcript annotations including
 * listing, generating, and retrieving annotation summaries.
 */

import { apiClient } from "./api";
import type {
  AnnotationListResponse,
  AnnotationSummaryResponse,
  AnnotationsBySegmentResponse,
  AnnotationsByTypeResponse,
  GenerateAnnotationsRequest,
  GenerateAnnotationsResponse,
  SegmentAnnotationResponse,
} from "@/types/annotation";

const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";

/**
 * Get all annotations for a transcript
 */
export async function getAnnotations(
  transcriptId: string,
  options?: {
    therapyStyleId?: string;
    annotationType?: string;
  }
): Promise<AnnotationListResponse> {
  const response = await apiClient.get(
    `${API_PREFIX}/transcripts/${transcriptId}/annotations`,
    {
      params: {
        therapy_style_id: options?.therapyStyleId,
        annotation_type: options?.annotationType,
      },
    }
  );
  return response.data;
}

/**
 * Get annotation summary for a transcript
 */
export async function getAnnotationSummary(
  transcriptId: string,
  therapyStyleId?: string
): Promise<AnnotationSummaryResponse | null> {
  const response = await apiClient.get(
    `${API_PREFIX}/transcripts/${transcriptId}/annotations/summary`,
    {
      params: {
        therapy_style_id: therapyStyleId,
      },
    }
  );
  return response.data;
}

/**
 * Get annotations grouped by segment
 */
export async function getAnnotationsBySegment(
  transcriptId: string,
  therapyStyleId?: string
): Promise<AnnotationsBySegmentResponse[]> {
  const response = await apiClient.get(
    `${API_PREFIX}/transcripts/${transcriptId}/annotations/by-segment`,
    {
      params: {
        therapy_style_id: therapyStyleId,
      },
    }
  );
  return response.data;
}

/**
 * Get annotations grouped by type
 */
export async function getAnnotationsByType(
  transcriptId: string,
  therapyStyleId?: string
): Promise<AnnotationsByTypeResponse[]> {
  const response = await apiClient.get(
    `${API_PREFIX}/transcripts/${transcriptId}/annotations/by-type`,
    {
      params: {
        therapy_style_id: therapyStyleId,
      },
    }
  );
  return response.data;
}

/**
 * Trigger annotation generation for a transcript
 */
export async function generateAnnotations(
  transcriptId: string,
  request?: GenerateAnnotationsRequest
): Promise<GenerateAnnotationsResponse> {
  const response = await apiClient.post(
    `${API_PREFIX}/transcripts/${transcriptId}/annotations/generate`,
    request || {}
  );
  return response.data;
}

/**
 * Get annotations for a specific segment
 */
export async function getSegmentAnnotations(
  segmentId: string,
  therapyStyleId?: string
): Promise<SegmentAnnotationResponse[]> {
  const response = await apiClient.get(
    `${API_PREFIX}/segments/${segmentId}/annotations`,
    {
      params: {
        therapy_style_id: therapyStyleId,
      },
    }
  );
  return response.data;
}
